import { ReactElement, useCallback, useRef, useState } from 'react';
import { NavigateFunction } from 'react-router';
import styled from 'styled-components';
import { Household, Role, User } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import { naturalStringCompare } from '../../../../common/src/util.ts';
import PencilIcon from '../../assets/pencil.svg?react';
import { LocalStorageKey } from '../../data/client_local_storage';
import { useHousehold } from '../../data/use_household';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert } from '../../util/confirm';
import { SettingsTransitionWrapper } from '../../util/shared';
import { Header } from '../header';
import { Button, LeftAlignedButton } from './button';
import { ButtonContent, goToNextOnboardingStep, isAdminInvite, Message, RoleTokenQueryString } from './common';
import { EditUser } from './edit_user.tsx';
import { Frame } from './frame';

export interface Props {
  onBack?(): void;
  onSelectUser?: (user: User) => void;
  navigate?: NavigateFunction;
  prompt?: string;
  startInEditMode?: boolean; // open with the edit pencils showing (the Household page's Edit Members row)
}

export function getHouseholdSettingsPageTitle(household?: Household) {
  return `The ${household?.familyLastName} Household`;
}

export const ChooseUser = ({ onBack, onSelectUser, navigate, prompt, startInEditMode }: Props) => {
  const inOnboarding = window.location.href.includes('onboarding');
  const [isEditingMember, setIsEditingMember] = useState(false);
  const { household, setHousehold } = useHousehold();
  const [activeUserToken, setActiveUserToken] = useLocalStorage(LocalStorageKey.ActiveUserToken);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [isEditingUsers, setIsEditingUsers] = useState(!!startInEditMode);
  const [userToEdit, setUserToEdit] = useState<User | undefined>(undefined);
  const noHouseholdUsers = !household?.users?.length;
  const params = new URLSearchParams(window.location.search);
  const role = params.get(RoleTokenQueryString) ?? Role.Administrator;
  const isDialog = !navigate;

  const editUsers = useCallback(() => {
    if (isEditingUsers) {
      setIsEditingUsers(false);
    } else {
      setIsEditingUsers(true);
    }
  }, [isEditingUsers]);

  const handleUserClick = useCallback(
    (user: User) => {
      if (onSelectUser) {
        return onSelectUser(user);
      }
      if (!isEditingUsers) {
        setActiveUserToken(user.token);
        if (!inOnboarding && onBack) {
          setTimeout(onBack, 250);
        }
      } else {
        setUserToEdit(user);
        setIsEditingMember(true);
      }
    },
    [onSelectUser, isEditingUsers, setActiveUserToken, inOnboarding, onBack],
  );

  const addNewMember = useCallback(() => {
    setUserToEdit(undefined);
    setIsEditingMember(true);
  }, []);

  const renderHouseholdMembers = () => {
    return (
      <ButtonContent ref={scrollableRef}>
        {(household?.users ?? [])
          .sort((lhs, rhs) => naturalStringCompare(lhs.name, rhs.name))
          .map((user) => {
            return (
              <MemberButton
                user={user}
                key={user.token}
                isSelected={user.token === activeUserToken}
                isEditingUsers={isEditingUsers}
                onClick={handleUserClick}
              />
            );
          })}
      </ButtonContent>
    );
  };

  const onContinue = useCallback(async () => {
    if (!inOnboarding || !navigate) {
      return;
    }
    if (!activeUserToken) {
      void alert({
        confirmation: <span>Please select a user</span>,
      });
      return;
    }
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.ChooseUser, action: 'userChosen' });
  }, [activeUserToken, inOnboarding, navigate]);

  const onBackFromEditUser = useCallback(() => {
    setIsEditingMember(false);
  }, []);

  const onDoneEditingMember = useCallback(() => {
    // The Edit Members surface (startInEditMode) stays in edit mode across add/edit/delete;
    // elsewhere a completed edit drops back to member-selection mode.
    if (!startInEditMode) {
      setIsEditingUsers(false);
    }
    setIsEditingMember(false);
  }, [startInEditMode]);

  // The header goes inside the Frame (via EditUser's header prop) so the frame's
  // --view-height budget includes it; stacking it above pushed the footer buttons
  // below the fold on short screens.
  const wrapWithTransition = useCallback(
    (component: ReactElement, isVisible: boolean) => (
      <SettingsTransitionWrapper $left={isVisible ? '0' : '100%'}>{component}</SettingsTransitionWrapper>
    ),
    [],
  );

  const renderFooterButtons = () => {
    let bottomButtonTitle: string;
    if (inOnboarding) {
      bottomButtonTitle = 'Continue';
    } else if (isEditingUsers) {
      bottomButtonTitle = 'Done';
    } else {
      bottomButtonTitle = 'Edit Members';
    }

    return (
      <>
        <ButtonWrapper $hidden={!inOnboarding && !isEditingUsers && !isDialog}>
          <Button onClick={addNewMember} notPrimary={!!activeUserToken}>
            {'Add New Member'}
          </Button>
        </ButtonWrapper>
        {/* The Edit Members surface (startInEditMode) is purely for editing, so it has no
            Done button; onboarding keeps its Continue button for selecting the active member. */}
        {!startInEditMode && (
          <ButtonWrapper $hidden={isDialog}>
            <Button onClick={inOnboarding ? onContinue : editUsers} disabled={!activeUserToken}>
              {bottomButtonTitle}
            </Button>
          </ButtonWrapper>
        )}
      </>
    );
  };

  const title = userToEdit ? 'Edit Household Member' : 'Add Household Member';

  return (
    <>
      <Frame
        onboardingPage={OnboardingPages.ChooseUser}
        header={isDialog ? undefined : <Header title={getHouseholdSettingsPageTitle(household)} onBack={onBack} />}
        mainContent={
          <>
            <WhoAreYou $widthInChars={prompt || isAdminInvite() ? 19 : 11} $isDialog={isDialog}>
              {prompt ??
                (isAdminInvite()
                  ? `Who is ${role === Role.Administrator ? 'the administrator' : 'on staff'}?`
                  : 'Who are you?')}
            </WhoAreYou>
            {renderHouseholdMembers()}
          </>
        }
        alignTop={true}
        footerContent={renderFooterButtons()}
        onBack={onBack}
        onEnterKeypress={noHouseholdUsers ? addNewMember : inOnboarding ? onContinue : undefined}
        suppressBackButton={true}
        embed={isDialog}
      />

      {wrapWithTransition(
        <EditUser
          user={userToEdit}
          onDone={onDoneEditingMember}
          household={household}
          setHousehold={setHousehold}
          isVisible={isEditingMember}
          navigate={navigate}
          header={<Header title={title} onBack={onBackFromEditUser} />}
        />,
        isEditingMember,
      )}
    </>
  );
};

const WhoAreYou = styled(Message)<{ $isDialog: boolean }>`
  padding-top: ${(props) => (props.$isDialog ? '0.5em' : '2em')};
  padding-bottom: 1em;
`;

const UserWrapper = styled.div`
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; // TODO(hewitt): determine why ellipsis is not working
  text-align: left;
  padding: 15px;
`;

const UserName = styled.div``;

const UserEmail = styled.div`
  font-family: Jost-Regular, sans-serif;
  color: var(--color-text-light);
`;

const Pencil = styled(PencilIcon)<{ $visible?: boolean }>`
  display: ${(props) => (props.$visible ? 'inherit' : 'none')};
  width: 30px;
  padding-left: 10px;
  cursor: pointer;
  fill: var(--color-text);
  position: absolute;
  margin-left: 87%;
`;

const ButtonWrapper = styled.div<{ $hidden?: boolean }>`
  display: ${(props) => (props.$hidden ? 'none' : 'inherit')};
  width: 100%;
  justify-content: center;
`;

function MemberButton({
  user,
  isSelected,
  isEditingUsers,
  onClick,
}: {
  user: User;
  isSelected: boolean;
  isEditingUsers: boolean;
  onClick: (user: User) => void;
}) {
  const onClickUser = useCallback(() => {
    onClick(user);
  }, [onClick, user]);
  return (
    <LeftAlignedButton selected={isSelected} onClick={onClickUser} notPrimary={true}>
      <UserWrapper>
        <UserName>{user.name}</UserName>
        <UserEmail style={{ display: 'none' }}>{user.email}</UserEmail>
      </UserWrapper>
      <Pencil $visible={isEditingUsers} />
    </LeftAlignedButton>
  );
}
